import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  IconButton,
  Markdown,
  Separator,
  Slider,
  Skeleton,
  toast
} from 'design-system';
import { ArrowLeftIcon, StarHalfIcon, StarIcon, XIcon } from 'lucide-react';

import { useUserDetails } from '@/features/_shared/user';
import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';
import { captureEvent } from '@/services/analytics';
import { clock } from '@/services/clock';

import { feedArticleOptions } from '../Feed/components/feedArticle.options';
import { RecapBlockquote, RecapH3, RecapHeadingsContext } from './lib/recapMarkdownComponents';
import { useMarkdownHeadings } from './lib/useMarkdownHeadings';
import { useScrollSpy } from './lib/useScrollSpy';
import { MonthlyRecapTableOfContents } from './MonthlyRecapTableOfContents';

type MonthlyRecapArticleDialogProps = {
  article: MonthlyRecapsArticle;
  onBack: () => void;
  onClose: () => void;
};

const recapMarkdownOverrides = { blockquote: RecapBlockquote, h3: RecapH3 };

const getAriaValueText = (_formattedValue: string, value: number) =>
  value === 0 ? 'Not rated' : `${value} out of 5 stars`;

const getRatingValue = (value: number | readonly number[]) =>
  typeof value === 'number' ? value : (value[0] ?? 0);

const RatingStars = ({ value }: { value: number }) => (
  <div
    aria-hidden
    className='flex gap-1 text-gx-highlight'
  >
    {[1, 2, 3, 4, 5].map(star => {
      if (value >= star) {
        return (
          <StarIcon
            key={star}
            className='size-6 fill-current'
          />
        );
      }
      if (value >= star - 0.5) {
        return (
          <StarHalfIcon
            key={star}
            className='size-6 fill-current'
          />
        );
      }
      return (
        <StarIcon
          key={star}
          className='size-6'
        />
      );
    })}
  </div>
);

export const MonthlyRecapArticleDialog = ({
  article,
  onBack,
  onClose
}: MonthlyRecapArticleDialogProps) => {
  const { userEmail } = useUserDetails();
  const [rating, setRating] = useState<number | null>(null);
  const [groupName, fileName] = article.file.split('/');
  const contentReadyAt = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    data: markdownContent,
    isLoading,
    isError
  } = useQuery(feedArticleOptions.file(groupName, fileName, true));

  const headings = useMarkdownHeadings(markdownContent ?? '');
  const sectionIds = useMemo(() => headings.map(heading => heading.id), [headings]);
  const { activeId } = useScrollSpy(sectionIds, contentRef);

  const handleSelectHeading = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!isLoading && !isError && markdownContent) {
      contentReadyAt.current = clock.now();
    }
  }, [isLoading, isError, markdownContent]);

  const captureReadingTime = useEffectEvent(() => {
    if (contentReadyAt.current !== null) {
      captureEvent('feed_article_reading_time', {
        file_name: fileName,
        group_name: groupName,
        reading_time_seconds: Math.round((clock.now() - contentReadyAt.current) / 1000)
      });
      contentReadyAt.current = null;
    }
  });

  useEffect(() => {
    return () => {
      captureReadingTime();
    };
  }, []);

  const submitRating = (value: number) => {
    if (value <= 0) return;

    captureEvent('monthly_recap_rated', {
      email: userEmail,
      rating: value,
      title: article.headline,
      month: article.month,
      region: article.region,
      date: article.date,
      read_time: article.readTime,
      timestamp: clock.date().toISOString()
    });
    toast.add({ title: 'Thanks for rating!', type: 'success' });
  };

  return (
    <div className='flex min-h-0 flex-1 flex-col bg-background'>
      <header className='flex h-16 shrink-0 items-center border-b bg-background'>
        <IconButton
          className='h-full rounded-none border-r'
          label='Back'
          onClick={onBack}
        >
          <ArrowLeftIcon aria-hidden />
        </IconButton>

        <div className='flex min-w-0 flex-1 items-center gap-2 px-4'>
          <span className='truncate text-xl font-semibold'>Monthly recap</span>
          <span aria-hidden>·</span>
          <span className='truncate text-xl font-semibold'>{article.region}</span>
        </div>

        <IconButton
          className='h-full rounded-none border-l'
          label='Close'
          onClick={onClose}
        >
          <XIcon aria-hidden />
        </IconButton>
      </header>

      <div className='flex min-h-0 flex-1'>
        <aside className='hidden w-64 shrink-0 overflow-y-auto border-r md:block'>
          <MonthlyRecapTableOfContents
            activeId={activeId}
            headings={headings}
            onSelect={handleSelectHeading}
          />
        </aside>

        <div
          ref={contentRef}
          className='min-w-0 flex-1 overflow-y-auto'
        >
          <article className='mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-8'>
            <div className='flex flex-wrap gap-2'>
              <Badge>{article.month}</Badge>
              <Badge>{article.region}</Badge>
            </div>

            <div>
              <h1 className='text-3xl font-bold text-foreground'>{article.headline}</h1>
              <p className='mt-2 text-sm text-muted-foreground'>
                Date: {article.date}
                {article.readTime ? ` · ${article.readTime}` : ''}
              </p>
            </div>

            {isLoading ? (
              <output
                aria-label='Loading article'
                className='space-y-3'
              >
                <Skeleton className='h-6 w-2/3' />
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-5/6' />
                <Skeleton className='h-4 w-4/6' />
              </output>
            ) : null}

            {isError ? (
              <Alert>
                <AlertTitle>Article unavailable</AlertTitle>
                <AlertDescription>Failed to load article content.</AlertDescription>
              </Alert>
            ) : null}

            {markdownContent ? (
              <RecapHeadingsContext value={headings}>
                <Markdown components={recapMarkdownOverrides}>{markdownContent}</Markdown>
              </RecapHeadingsContext>
            ) : null}

            <Separator />

            <section
              aria-labelledby='monthly-recap-rating-title'
              className='mx-auto flex w-full max-w-md flex-col items-center gap-3 py-4 text-center'
            >
              <h2
                className='text-xl font-semibold'
                id='monthly-recap-rating-title'
              >
                Was this article valuable?
              </h2>
              <p className='text-sm text-muted-foreground'>
                Rate this month&apos;s recap to help us improve.
              </p>
              <RatingStars value={rating ?? 0} />
              <Slider
                className='w-full'
                getAriaLabel={() => 'Rate this article'}
                getAriaValueText={getAriaValueText}
                max={5}
                min={0}
                step={0.5}
                value={[rating ?? 0]}
                onValueChange={value => {
                  setRating(getRatingValue(value));
                }}
                onValueCommitted={value => {
                  submitRating(getRatingValue(value));
                }}
              />
            </section>
          </article>
        </div>
      </div>
    </div>
  );
};
